import { type ChatbotParams, ChatCustomBlockTypes, type ChatMessage } from "@do/shared-types";
import { Box, Grow } from "@mui/material";
import { useTranslation } from "next-i18next";
import { type FC, memo, type PropsWithChildren, type ReactElement } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { shallowEqualObjects } from "shallow-equal";
import { type ActionProps, BouncingDots, FastAction } from "../atoms";
import { ChatMessageText } from "./ChatMessageText";
import { ChatSteps } from "./ChatSteps";

export type EditBotResponseProps = {
  isTestingMode?: boolean;
  onEditBotResponse?: (messageId: string, openDialog: boolean) => void;
};
export type ChatMessageUiProps = ChatMessage &
  ActionProps &
  EditBotResponseProps & {
    chatbotParams: ChatbotParams;
    isPreviewMode?: boolean;
    mergedCtxData?: Record<string, string> | null;
  };

export const ChatMessageUi: FC<ChatMessageUiProps> = memo(
  ({
    blocks,
    chatbotParams,
    disabled,
    onEditBotResponse = () => {},
    id,
    user,
    mergedCtxData,
    metadata,
    onFastAction,
    isPreviewMode = false,
    isTestingMode,
  }) => {
    const metadataStr = JSON.stringify(Object.assign({}, ...(metadata || [])));
    const { t } = useTranslation("common");
    const paddingProps = user ? { pt: 2, pb: 1 } : { pt: 1 };

    return ((blocks?.map((block, idx) => {
      return (
        block.type !== ChatCustomBlockTypes.liveChat && (
          <Grow in key={`${block.id}-${idx}`}>
            <Box
              data-metadata={metadataStr}
              display="flex"
              flexDirection="column"
              id={id}
              px={2}
              {...paddingProps}
              width="100%"
            >
              {block.type === ChatCustomBlockTypes.text && (
                <ChatMessageText
                  {...block}
                  disabled={!!disabled}
                  id={id}
                  idx={idx}
                  isTestingMode={!!isTestingMode}
                  metadata={metadata}
                  mergeCtxData={mergedCtxData}
                  onEditBotResponse={onEditBotResponse}
                  user={!!user}
                />
              )}
              {block.type === ChatCustomBlockTypes.bouncingDots && <BouncingDots />}
              {block.type === ChatCustomBlockTypes.buttons &&
                block.buttons &&
                block.buttons.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={1} maxWidth="100%">
                    {block.buttons.map((button, idx) => (
                      <FastAction
                        key={`${block.id}-fast-action-${idx}`}
                        disabled={disabled}
                        isPreviewMode={isPreviewMode}
                        onFastAction={onFastAction}
                        id={`${block.id}-button-${idx}`}
                        {...button}
                      />
                    ))}
                  </Box>
                )}
              {block.type === ChatCustomBlockTypes.steps && (
                <ErrorBoundary
                  fallback={
                    <ChatMessageText
                      text={t("fsmDefError")}
                      user={false}
                      disabled={false}
                      id={id}
                      idx={idx}
                      isTestingMode={!!isTestingMode}
                      type={ChatCustomBlockTypes.text}
                    />
                  }
                >
                  <ChatSteps
                    chatbotParams={chatbotParams}
                    disabled={disabled}
                    onFastAction={onFastAction}
                    id={id}
                    fsmDef={block.fsmDef}
                    mergedCtxData={mergedCtxData}
                  />
                </ErrorBoundary>
              )}
            </Box>
          </Grow>
        )
      );
    }) as unknown as ReactElement) || null) as ReturnType<FC>;
  },
  (
    prevProps: Readonly<PropsWithChildren<PropsWithChildren<ChatMessageUiProps>>>,
    nextProps: Readonly<PropsWithChildren<PropsWithChildren<ChatMessageUiProps>>>
  ) => {
    return shallowEqualObjects(prevProps, nextProps);
  }
);
